/**
 * Veli girişi: telefon ya da e-postaya gelen kod.
 *
 * Veli parola kullanmıyor. Sebebi: portal yılda birkaç kez açılıyor,
 * unutulan parolanın sıfırlanması yine bu kanallara dayanacaktı; arada
 * bir de zayıf parolaları saklamış olurduk.
 *
 * E-posta öne alındı, çünkü SMS başlığı için KEP ve e-imza gerekiyor;
 * o evrak işi bitene kadar e-posta çalışan tek otomatik kanal. Üçüncü
 * yol da var ama bu ekranda görünmüyor: uygulayıcının gönderdiği tek
 * kullanımlık bağlantı.
 *
 * "Bu adres kayıtlı değil" diye bir uyarı YOK. Sunucu da ayrım
 * yapmıyor: bir numaranın ya da adresin bu merkeze danışan gönderip
 * göndermediği bilgisi, tek başına sağlık verisidir.
 */

import { useState } from 'react'
import { ApiHatasi, api, type Kanal, type Veli } from '../api'

export function Giris({
  onGiris,
  baglantiHatasi,
}: {
  onGiris: (v: Veli) => Promise<void>
  baglantiHatasi?: boolean
}) {
  const [kanal, setKanal] = useState<Kanal>('eposta')
  const [adim, setAdim] = useState<'kimlik' | 'kod'>('kimlik')
  const [deger, setDeger] = useState('')
  const [kod, setKod] = useState('')
  const [maske, setMaske] = useState('')
  const [dakika, setDakika] = useState(5)
  const [denemeKodu, setDenemeKodu] = useState<string | null>(null)
  const [hata, setHata] = useState<string | null>(null)
  const [bekliyor, setBekliyor] = useState(false)

  const epostaIle = kanal === 'eposta'

  const kodIste = async (e: React.FormEvent) => {
    e.preventDefault()
    setHata(null)
    setBekliyor(true)
    try {
      const r = await api.kodIste(kanal, deger.trim())
      setMaske(r.maske)
      setDakika(Math.max(1, Math.round(r.gecerlilikSn / 60)))
      setDenemeKodu(r.denemeKodu ?? null)
      setAdim('kod')
    } catch (err) {
      const k = err instanceof ApiHatasi ? err.kod : ''
      setHata(
        k === 'cok_fazla_istek'
          ? 'Çok fazla kod istendi. 15 dakika sonra tekrar deneyin.'
          : k === 'eposta_gonderilemedi'
            ? 'Şu anda e-posta gönderilemiyor. Merkezle görüşün.'
            : k === 'sms_gonderilemedi'
              ? 'Şu anda SMS gönderilemiyor. E-posta ile deneyin ya da merkezle görüşün.'
              : 'Bir sorun oldu, tekrar deneyin.',
      )
    } finally {
      setBekliyor(false)
    }
  }

  const girisYap = async (e: React.FormEvent) => {
    e.preventDefault()
    setHata(null)
    setBekliyor(true)
    try {
      const r = await api.giris(kanal, deger.trim(), kod.trim())
      await onGiris(r.veli)
    } catch (err) {
      if (err instanceof ApiHatasi && err.kod === 'deneme_bitti') {
        setHata('Kod birkaç kez yanlış girildi. Yeni kod isteyin.')
        setAdim('kimlik')
        setKod('')
      } else {
        setHata('Kod doğru değil ya da süresi doldu.')
      }
    } finally {
      setBekliyor(false)
    }
  }

  const kanalDegistir = (y: Kanal) => {
    setKanal(y)
    setDeger('')
    setHata(null)
    setAdim('kimlik')
  }

  return (
    <div className="giris">
      <h1>Nirengi</h1>
      <p className="alt">Veli portalı</p>

      {baglantiHatasi && (
        <div className="uyari">
          Açtığınız bağlantı geçersiz ya da süresi dolmuş. Bağlantılar bir kez kullanılıyor;
          merkezden yenisini isteyebilir ya da aşağıdan kod alarak girebilirsiniz.
        </div>
      )}

      {adim === 'kimlik' ? (
        <form className="kart" onSubmit={kodIste}>
          {hata && <div className="uyari">{hata}</div>}

          <div className="dugmeler" style={{ marginTop: 0, marginBottom: 14 }}>
            <button
              type="button"
              className={epostaIle ? 'birincil' : ''}
              onClick={() => kanalDegistir('eposta')}
            >
              E-posta
            </button>
            <button
              type="button"
              className={!epostaIle ? 'birincil' : ''}
              onClick={() => kanalDegistir('sms')}
            >
              SMS
            </button>
          </div>

          <label>
            {epostaIle ? 'E-posta adresiniz' : 'Telefon numaranız'}
            <input
              type={epostaIle ? 'email' : 'tel'}
              inputMode={epostaIle ? 'email' : 'tel'}
              autoComplete={epostaIle ? 'email' : 'tel'}
              placeholder={epostaIle ? 'ornek@eposta.com' : '0532 000 00 00'}
              value={deger}
              onChange={(e) => setDeger(e.target.value)}
              required
            />
          </label>
          <button className="birincil" type="submit" disabled={bekliyor} style={{ width: '100%' }}>
            {bekliyor ? 'Gönderiliyor…' : 'Kod gönder'}
          </button>
          <p className="alt" style={{ margin: '12px 0 0' }}>
            Merkeze bildirdiğiniz {epostaIle ? 'adresi' : 'numarayı'} yazın. Altı haneli bir kod
            göndereceğiz.
          </p>
        </form>
      ) : (
        <form className="kart" onSubmit={girisYap}>
          {hata && <div className="uyari">{hata}</div>}
          <p style={{ marginTop: 0 }}>
            Kod <strong>{maske}</strong> adresine gönderildi. {dakika} dakika geçerli.
          </p>
          {denemeKodu && (
            <div className="kisit" style={{ marginTop: 0, marginBottom: 14 }}>
              <strong>Deneme kurulumu.</strong> Gerçek SMS sağlayıcısı henüz bağlı değil, bu
              yüzden kod burada gösteriliyor: <strong>{denemeKodu}</strong>
            </div>
          )}
          <label>
            Gelen kod
            <input
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="\d{6}"
              maxLength={6}
              placeholder="000000"
              value={kod}
              onChange={(e) => setKod(e.target.value.replace(/\D/g, ''))}
              required
              style={{ fontSize: 22, letterSpacing: '0.3em', textAlign: 'center' }}
            />
          </label>
          <button
            className="birincil"
            type="submit"
            disabled={bekliyor || kod.length !== 6}
            style={{ width: '100%' }}
          >
            {bekliyor ? 'Giriliyor…' : 'Giriş'}
          </button>
          <div className="dugmeler">
            <button
              type="button"
              onClick={() => {
                setAdim('kimlik')
                setKod('')
                setHata(null)
              }}
            >
              {epostaIle ? 'Adresi değiştir' : 'Numarayı değiştir'}
            </button>
          </div>
        </form>
      )}

      <p className="alt" style={{ textAlign: 'center' }}>
        Burada çocuğunuzun ödevlerini, randevularını ve size gönderilen raporları
        görebilirsiniz.
      </p>
    </div>
  )
}
